import { useCallback, useEffect, useMemo, useState } from 'react'
import { useElementSize } from '@mantine/hooks'
import { ClientLogo } from 'common/components'
import { useMessageActions, useSelector } from 'common/hooks'
import { APPLICATION_ID, getApplicationLogoPath } from 'common/utils'
import { isNil } from 'lodash'
import { ROUTES } from 'services'
import { UserMenu } from 'user/components'

import Menu from './Menu'

const ACTION_ICON_BUTTON_WIDTH = 45
const BASIC_GRID_GAP = 8

export default function HeaderWrapper() {
  const { menu } = useSelector((state) => state.iframe)
  const { sendApprovalBackToHomeFromDetailsMessage } = useMessageActions()
  const { ref: logoRef, width: logoWidth } = useElementSize()
  const { ref: navbarWrapperRef, width: navbarWrapperWidth } = useElementSize()
  const { ref: navbarRef } = useElementSize()
  const { ref: userMenuRef, width: userMenuWidth } = useElementSize()

  const [visibleLinkCount, setVisibleLinkCount] = useState<number | null>(null)
  const [linksWidth, setLinksWidth] = useState<number[]>([])
  const [isDropdownMenuVisible, setIsDropdownMenuVisible] = useState(false)

  const applicationId = useMemo(() => localStorage.getItem('application_id'), [])

  const leftWidthSpaceForNavbarInHeader = useMemo(
    () =>
      navbarWrapperWidth -
      (logoWidth + userMenuWidth + ACTION_ICON_BUTTON_WIDTH + BASIC_GRID_GAP * 3),
    [navbarWrapperWidth, logoWidth, userMenuWidth],
  )

  const handleResize = useCallback(() => {
    if (navbarRef?.current && menu) {
      let totalWidth = 0
      let count = 0

      for (let i = 0; i < linksWidth.length; i++) {
        totalWidth += linksWidth[i] + BASIC_GRID_GAP
        if (totalWidth > leftWidthSpaceForNavbarInHeader) {
          break
        }
        count++
      }
      setVisibleLinkCount(count)
    }
  }, [linksWidth, menu, navbarRef, leftWidthSpaceForNavbarInHeader])

  const handleRedirectToHomePage = useCallback(
    () => sendApprovalBackToHomeFromDetailsMessage({ to: 'HOME' }),
    [sendApprovalBackToHomeFromDetailsMessage],
  )

  useEffect(() => {
    const handleWindowResize = () => {
      handleResize()
      setIsDropdownMenuVisible(false)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [handleResize])

  useEffect(() => {
    if (navbarRef.current && menu) {
      const nav = navbarRef.current?.children[0]?.children as HTMLAnchorElement[]

      if (nav.length === menu.length) {
        setLinksWidth(Array.from(nav).map((item) => item.clientWidth))
      }
    }
  }, [navbarRef, menu])

  useEffect(() => {
    if (menu) {
      handleResize()
    }
  }, [menu, handleResize])

  return (
    <div
      className={'flex items-center justify-between h-[60px] min-w-0 gap-4'}
      ref={navbarWrapperRef}
    >
      <div className={'flex items-center flex-1 min-w-0 gap-4'}>
        <div className={'flex items-center flex-shrink-0'} ref={logoRef}>
          <ClientLogo
            classes={{
              link:
                applicationId === APPLICATION_ID.CTK_LOGISTICS ? 'h-[32px]' : 'h-[24px]',
            }}
            {...getApplicationLogoPath()}
            link={ROUTES.home()}
            onClick={handleRedirectToHomePage}
          />
        </div>
        {menu && (
          <div className={'flex-1 min-w-0'}>
            <Menu
              hiddenMenu={menu.slice(
                isNil(visibleLinkCount) ? menu.length : visibleLinkCount,
                menu.length,
              )}
              isDropdownMenuVisible={isDropdownMenuVisible}
              ref={navbarRef}
              setIsDropdownMenuVisible={setIsDropdownMenuVisible}
              visibleMenu={menu.slice(
                0,
                visibleLinkCount === 0 ? 0 : visibleLinkCount || menu.length,
              )}
            />
          </div>
        )}
      </div>
      <div className={'flex items-center flex-shrink-0 gap-4'} ref={userMenuRef}>
        <UserMenu />
      </div>
    </div>
  )
}
